/*
 * ZeroWheel carousel and slideshow (docs/specs/zw-carousel.md, decisions 83–90).
 * - Swiping and trackpads use the browser's own scrolling and snapping; this script adds the rest: dots, mouse
 *   drag, clicking a peeking slide to bring it in, the arrow keys on the dots, and auto-rotate with pause/play.
 * - A position is where the row can rest: one per slide on phones, slides minus (in view − 1) on desktop. Positions
 *   are measured from the slides, so every screen size uses the same code.
 * - Auto-rotate runs only while the carousel is half on screen (or fills half the screen), starts from slide 1 on each
 *   visit, and stops for good once the visitor taps, swipes sideways, clicks, drags, scrolls sideways or uses a dot
 *   (pause/play starts it again). A finger scrolling the page up or down doesn't stop it. It starts stopped with
 *   reduced motion turned on, and in the theme editor (play previews it there).
 * - Moves this script makes (auto-rotate, dots, arrow keys, a peeking slide, the end of a drag) settle in one second on
 *   a spring with no bounce. The row jumps to its new position at once and its slides are drawn back where they were,
 *   then slide in with a transform animation (keyframes along the spring), which the browser runs off the main thread,
 *   so a busy page doesn't make it stutter.
 * - A visitor who touches or scrolls the row mid-move takes it over where it's drawn: it scrolls freely under their
 *   finger, and once they let go it settles on the nearest position. A finger that turns out to be scrolling the page
 *   lets the move carry on.
 * Markup and styles: blocks/zw-carousel.liquid.
 */

/** Pixels a mouse moves before a press becomes a drag instead of a click. */
const DRAG_THRESHOLD = 5;
/** Positions closer than this are the same place (rounding). */
const TOLERANCE = 2;
/** Length of a move, and how long the row waits after a visitor's scrolling stops before settling. */
const MOVE_DURATION = 1000;
const SETTLE_DELAY = 150;
/**
 * The move's spring has no bounce (critically damped): the slides' offset from where they rest is
 * (a + b·t)·e^(−ωt), t in seconds. ω = 10 per second leaves 0.05% of the distance at one second, under a pixel on
 * the widest move.
 */
const OMEGA = 10;
/** Keyframes per move; the browser draws straight lines between them. */
const MOVE_KEYFRAMES = 60;

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class ZwCarousel extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** Scroll offsets the row can rest at, ascending. @type {number[]} */
  #positions = [];
  #current = 0;

  /** Milliseconds per slide; 0 when auto-rotate is off. */
  #interval = 0;
  #stopped = false;
  #inView = false;
  /** @type {number | undefined} */
  #timer;

  /** @type {{ x: number, scrollLeft: number, moved: boolean, controller: AbortController } | null} */
  #drag = null;
  #suppressClick = false;

  /** The running move: its slides' animations and its spring's a and b (see OMEGA). */
  /** @type {{ animations: Animation[], a: number, b: number } | null} */
  #move = null;
  /** A visitor took over mid-move: snapping is off until they let go and the row settles. */
  #held = false;
  #touching = false;
  /** Where a touch started, and whether it has turned out to move sideways ('x') or up and down ('y'). */
  /** @type {{ x: number, y: number, axis: 'x' | 'y' | null } | null} */
  #touch = null;
  /** @type {number | undefined} */
  #settleTimer;

  connectedCallback() {
    const track = this.#track;
    if (!track || this.#controller) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    const designMode = Boolean(/** @type {any} */ (window).Shopify?.designMode);
    this.#interval = Number(this.dataset.autoplay) || 0;
    this.#stopped = designMode || reducedMotion.matches;

    if ('onscrollend' in window) {
      track.addEventListener('scrollend', () => this.#updateCurrent(), { signal });
    } else {
      // Safari before 18.2: settle on the position once scrolling pauses.
      let scrollTimer = 0;
      track.addEventListener(
        'scroll',
        () => {
          clearTimeout(scrollTimer);
          scrollTimer = window.setTimeout(() => this.#updateCurrent(), 100);
        },
        { signal }
      );
    }

    track.addEventListener('scroll', () => this.#settleSoon(), { passive: true, signal });
    track.addEventListener('touchstart', (event) => this.#onTouchStart(event), { passive: true, signal });
    track.addEventListener('touchmove', (event) => this.#onTouchMove(event), { passive: true, signal });
    track.addEventListener('touchend', (event) => this.#onTouchEnd(event, true), { passive: true, signal });
    track.addEventListener('touchcancel', (event) => this.#onTouchEnd(event, false), { passive: true, signal });
    track.addEventListener('pointerdown', (event) => this.#onPointerDown(event), { signal });
    track.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
    track.addEventListener('click', (event) => this.#onClick(event), { capture: true, signal });
    track.addEventListener(
      'wheel',
      (event) => {
        if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
        this.#hold();
        this.#stop();
      },
      { passive: true, signal }
    );
    // Tabbing to a link in a slide that's out of view brings the slide in. Keyboard focus only: a mouse press focuses
    // the button too, and that click (or drag) is handled on its own.
    track.addEventListener(
      'focusin',
      (event) => {
        if (event.target instanceof Element && event.target.matches(':focus-visible')) {
          this.#bringIntoView(this.#slideOf(event.target), true);
        }
      },
      { signal }
    );

    this.#dots?.addEventListener('click', (event) => this.#onDotClick(event), { signal });
    this.#dots?.addEventListener('keydown', (event) => this.#onDotKeydown(event), { signal });
    this.#toggle?.addEventListener('click', () => (this.#stopped ? this.#play() : this.#stop()), { signal });

    document.addEventListener('visibilitychange', () => this.#schedule(), { signal });
    reducedMotion.addEventListener(
      'change',
      () => {
        if (reducedMotion.matches) this.#stop();
      },
      { signal }
    );
    document.addEventListener(
      'shopify:block:select',
      (event) => {
        const slide = this.#slideOf(event.target);
        if (slide) this.#goTo(this.#positionFor(slide, 'start'));
      },
      { signal }
    );

    // Positions only move when the track's width does; height changes (images and videos loading) are ignored.
    let width = -1;
    this.#resizeObserver = new ResizeObserver(([entry]) => {
      if (entry.contentRect.width === width) return;
      width = entry.contentRect.width;
      this.#measure();
    });
    this.#resizeObserver.observe(track);

    if (this.#interval) {
      this.#intersectionObserver = new IntersectionObserver(([entry]) => this.#onIntersect(entry), {
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5],
      });
      this.#intersectionObserver.observe(this);
    }

    this.#updateToggle();
    this.#measure();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#drag?.controller.abort();
    this.#drag = null;
    this.#resizeObserver?.disconnect();
    this.#intersectionObserver?.disconnect();
    clearTimeout(this.#timer);
    clearTimeout(this.#settleTimer);
    this.#cancelMove();
  }

  get #track() {
    return /** @type {HTMLElement | null} */ (this.querySelector(':scope > .zw-carousel__track'));
  }

  /** The track's children except the <style> tags a card writes beside itself (contrast-override). */
  get #slides() {
    return /** @type {HTMLElement[]} */ (
      [...(this.#track?.children ?? [])].filter((child) => !child.matches('style, script, template'))
    );
  }

  get #dots() {
    return /** @type {HTMLElement | null} */ (this.querySelector(':scope > .zw-carousel__controls .zw-carousel__dots'));
  }

  get #toggle() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector(':scope > .zw-carousel__controls .zw-carousel__toggle'));
  }

  /** The slide (a direct child of the track) holding a node. @param {EventTarget | null} node */
  #slideOf(node) {
    const track = this.#track;
    let element = node instanceof Element ? node : null;
    while (element && element.parentElement !== track) element = element.parentElement;
    return /** @type {HTMLElement | null} */ (element);
  }

  /** Seconds into the running move. */
  get #moveTime() {
    return Number(this.#move?.animations[0]?.currentTime ?? 0) / 1000;
  }

  /** How far a running move draws the slides from where they sit (px), 0 when none is running. */
  get #shift() {
    if (!this.#move) return 0;
    const { a, b } = this.#move;
    const t = this.#moveTime;
    return (a + b * t) * Math.exp(-OMEGA * t);
  }

  /** How fast that distance is changing (px per second). */
  get #shiftVelocity() {
    if (!this.#move) return 0;
    const { a, b } = this.#move;
    const t = this.#moveTime;
    return (b - OMEGA * (a + b * t)) * Math.exp(-OMEGA * t);
  }

  /** The scroll offset the row is drawn at: mid-move, the slides lag behind the row's actual scroll offset. */
  get #scroll() {
    return (this.#track?.scrollLeft ?? 0) - this.#shift;
  }

  /** A slide's resting offset: where the row scrolls to put the slide's start on the content column's left edge. */
  #offsetOf(/** @type {HTMLElement} */ slide) {
    const track = /** @type {HTMLElement} */ (this.#track);
    return slide.getBoundingClientRect().left - this.#shift - this.getBoundingClientRect().left + track.scrollLeft;
  }

  /** The largest scroll offset the row can reach. */
  get #maxScroll() {
    const track = /** @type {HTMLElement} */ (this.#track);
    return track.scrollWidth - track.clientWidth;
  }

  #measure() {
    // The positions are about to change, so a running move ends where it was going.
    this.#cancelMove();
    const slides = this.#slides;
    const max = this.#maxScroll;

    /** @type {number[]} */
    const positions = [];
    for (const slide of slides) {
      const offset = Math.min(Math.max(this.#offsetOf(slide), 0), max);
      if (!positions.some((position) => Math.abs(position - offset) < TOLERANCE)) positions.push(offset);
    }
    positions.sort((a, b) => a - b);
    this.#positions = positions;

    slides.forEach((slide, index) => {
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute('aria-label', this.#slideLabel(index + 1, slides.length));
    });

    this.#renderDots();
    this.#updateCurrent();
    // A pending timer keeps counting; this only starts one when there was nothing to rotate before.
    if (this.#timer === undefined) this.#schedule();
  }

  /** "Slide 2 of 5", from the theme's translation (accessibility.slide_status). @param {number} index @param {number} length */
  #slideLabel(index, length) {
    const template = this.dataset.labelSlide || 'Slide [index] of [length]';
    return template.replace('[index]', String(index)).replace('[length]', String(length));
  }

  /** The 1-based numbers of the slides fully in view at a position. @param {number} position */
  #slidesAt(position) {
    const width = this.clientWidth;
    /** @type {number[]} */
    const numbers = [];
    this.#slides.forEach((slide, index) => {
      const start = this.#offsetOf(slide) - position;
      if (start >= -TOLERANCE && start + slide.offsetWidth <= width + TOLERANCE) numbers.push(index + 1);
    });
    return numbers;
  }

  #renderDots() {
    const dots = this.#dots;
    const controls = dots?.parentElement;
    if (!dots || !controls) return;

    const count = this.#positions.length;
    controls.hidden = count <= 1;

    if (dots.children.length !== count) {
      dots.replaceChildren(
        ...this.#positions.map(() => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'zw-carousel__dot';
          return dot;
        })
      );
    }

    // Each dot is named for the first slide it brings in: "Slide 2 of 5" (accessibility.slide_status).
    const total = this.#slides.length;
    this.#positions.forEach((position, index) => {
      const first = this.#slidesAt(position)[0] ?? index + 1;
      dots.children[index]?.setAttribute('aria-label', this.#slideLabel(first, total));
    });
  }

  #nearestIndex() {
    const scrollLeft = this.#track?.scrollLeft ?? 0;
    let nearest = 0;
    this.#positions.forEach((position, index) => {
      if (Math.abs(position - scrollLeft) < Math.abs(this.#positions[nearest] - scrollLeft)) nearest = index;
    });
    return nearest;
  }

  #updateCurrent(index = this.#nearestIndex()) {
    this.#current = index;
    [...(this.#dots?.children ?? [])].forEach((dot, dotIndex) => {
      dot.setAttribute('aria-current', String(dotIndex === index));
    });
  }

  /**
   * Moves to a position on the spring: the row scrolls there at once, and its slides are drawn back where they were
   * and slide in. A new move starts where a running one has the slides and at its speed, so it carries on smoothly.
   * @param {number} index @param {{ instant?: boolean }} [options]
   */
  #goTo(index, { instant = false } = {}) {
    const track = this.#track;
    const position = this.#positions[index];
    if (!track || position === undefined) return;

    const from = this.#scroll;
    const velocity = this.#shiftVelocity;
    this.#cancelMove();
    track.scrollLeft = position;
    // Snapping comes back on only now that the row rests on a position, so the browser has nothing to snap.
    this.#release();
    this.#updateCurrent(index);

    const a = position - from;
    if (instant || reducedMotion.matches || (Math.abs(a) < 1 && Math.abs(velocity) < 1)) return;
    // Keeping the speed would carry a fast move past a nearer position; slowing to the fastest speed that stops
    // without passing it keeps the spring from bouncing.
    const b = a * (velocity + OMEGA * a) > 0 ? velocity + OMEGA * a : 0;
    /** @type {Keyframe[]} */
    const keyframes = Array.from({ length: MOVE_KEYFRAMES + 1 }, (_, frame) => {
      const t = ((frame / MOVE_KEYFRAMES) * MOVE_DURATION) / 1000;
      const offset = frame === MOVE_KEYFRAMES ? 0 : (a + b * t) * Math.exp(-OMEGA * t);
      return { transform: `translateX(${offset.toFixed(2)}px)` };
    });

    // Snapping is off for the move: the browser snaps to the slides where they're drawn, so it would follow them.
    track.classList.add('is-moving');
    const move = { animations: this.#slides.map((slide) => slide.animate(keyframes, MOVE_DURATION)), a, b };
    // Starting now rather than on the next frame, so a move that takes over doesn't hold still for a frame.
    const now = document.timeline.currentTime;
    if (now !== null) for (const animation of move.animations) animation.startTime = now;
    this.#move = move;
    move.animations[0]?.finished.then(
      () => {
        if (this.#move === move) this.#cancelMove();
      },
      () => {}
    );
  }

  /** Ends a running move where it was going. */
  #cancelMove() {
    for (const animation of this.#move?.animations ?? []) animation.cancel();
    this.#move = null;
    this.#track?.classList.remove('is-moving');
  }

  /** A visitor touches or scrolls the row mid-move: it stays where it's drawn and scrolls freely from there. */
  #hold() {
    const track = this.#track;
    if (!track || !this.#move) return;
    const at = this.#scroll;
    track.classList.add('is-held');
    this.#cancelMove();
    track.scrollLeft = at;
    this.#held = true;
    this.#settleSoon();
  }

  /** After a hold, once the visitor's scrolling stops and nothing touches the row, it settles on the nearest one. */
  #settleSoon() {
    if (!this.#held) return;
    clearTimeout(this.#settleTimer);
    this.#settleTimer = window.setTimeout(() => {
      if (!this.#touching && !this.#drag) this.#goTo(this.#nearestIndex());
    }, SETTLE_DELAY);
  }

  #release() {
    if (!this.#held) return;
    this.#held = false;
    clearTimeout(this.#settleTimer);
    this.#track?.classList.remove('is-held');
  }

  /**
   * The position that brings a slide fully into view: the slide comes in on the side it was peeking from.
   * @param {HTMLElement} slide
   * @param {'start' | 'nearest'} align - 'start' puts it first in view
   */
  #positionFor(slide, align) {
    const offset = this.#offsetOf(slide);
    const scrollLeft = this.#scroll;
    const target = align === 'nearest' && offset > scrollLeft ? offset + slide.offsetWidth - this.clientWidth : offset;
    const index = this.#positions.findIndex((position) => position >= target - TOLERANCE);
    return index === -1 ? this.#positions.length - 1 : index;
  }

  /** Whether a slide is wholly inside the content column. @param {HTMLElement} slide */
  #isInView(slide) {
    const column = this.getBoundingClientRect();
    const rect = slide.getBoundingClientRect();
    return rect.left >= column.left - TOLERANCE && rect.right <= column.right + TOLERANCE;
  }

  /** @param {HTMLElement | null} slide @param {boolean} [byVisitor] */
  #bringIntoView(slide, byVisitor = false) {
    if (!slide || this.#isInView(slide)) return false;
    if (byVisitor) this.#stop();
    this.#goTo(this.#positionFor(slide, 'nearest'));
    return true;
  }

  /** @param {TouchEvent} event */
  #onTouchStart(event) {
    this.#touching = true;
    const touch = event.touches[0];
    if (touch && event.touches.length === 1) this.#touch = { x: touch.clientX, y: touch.clientY, axis: null };
  }

  /** A touch moving sideways is the visitor's; one moving up or down scrolls the page. @param {TouchEvent} event */
  #onTouchMove(event) {
    const start = this.#touch;
    const touch = event.touches[0];
    if (!start || start.axis || !touch) return;
    const dx = Math.abs(touch.clientX - start.x);
    const dy = Math.abs(touch.clientY - start.y);
    if (Math.max(dx, dy) < DRAG_THRESHOLD) return;
    start.axis = dx > dy ? 'x' : 'y';
    if (start.axis === 'x') this.#stop();
    // Scrolling the page: the move that was held under the finger carries on.
    else if (this.#held) this.#goTo(this.#current);
  }

  /** @param {TouchEvent} event @param {boolean} ended - false when the browser cancelled the touch */
  #onTouchEnd(event, ended) {
    this.#touching = event.touches.length > 0;
    if (!this.#touching) {
      // A touch that never moved is a tap.
      if (ended && this.#touch && !this.#touch.axis) this.#stop();
      this.#touch = null;
    }
    this.#settleSoon();
  }

  /**
   * Holds a running move under the pointer. A mouse or pen press stops auto-rotate; a touch waits to see which way it
   * moves (#onTouchMove). Listens for the rest of a mouse drag only while one is in progress.
   * @param {PointerEvent} event
   */
  #onPointerDown(event) {
    this.#hold();
    if (event.pointerType !== 'touch') this.#stop();
    if (event.pointerType !== 'mouse' || event.button !== 0) return;

    this.#drag?.controller.abort();
    const controller = new AbortController();
    const { signal } = controller;
    this.#drag = { x: event.clientX, scrollLeft: this.#track?.scrollLeft ?? 0, moved: false, controller };
    window.addEventListener('pointermove', (moveEvent) => this.#onPointerMove(moveEvent), { signal });
    window.addEventListener('pointerup', () => this.#onPointerUp(), { signal });
    window.addEventListener('pointercancel', () => this.#onPointerUp(), { signal });
  }

  /** @param {PointerEvent} event */
  #onPointerMove(event) {
    const drag = this.#drag;
    const track = this.#track;
    if (!drag || !track) return;

    const distance = event.clientX - drag.x;
    if (!drag.moved && Math.abs(distance) < DRAG_THRESHOLD) return;
    drag.moved = true;
    track.classList.add('is-dragging');
    track.scrollLeft = drag.scrollLeft - distance;
  }

  #onPointerUp() {
    const drag = this.#drag;
    this.#drag = null;
    drag?.controller.abort();
    if (!drag?.moved) {
      this.#settleSoon();
      return;
    }

    this.#suppressClick = true;
    // The click that follows a drag arrives in this task; anything later is a new click.
    setTimeout(() => (this.#suppressClick = false));
    // Moving first, so snapping comes back on with the row already on a position.
    this.#goTo(this.#nearestIndex());
    this.#track?.classList.remove('is-dragging');
  }

  /** A drag never opens a link, and a click on a peeking slide brings it in instead. @param {MouseEvent} event */
  #onClick(event) {
    if (this.#suppressClick || this.#bringIntoView(this.#slideOf(event.target), true)) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.#suppressClick = false;
  }

  /** @param {MouseEvent} event */
  #onDotClick(event) {
    const dot = event.target instanceof Element ? event.target.closest('.zw-carousel__dot') : null;
    if (!dot) return;
    this.#stop();
    this.#goTo([...(this.#dots?.children ?? [])].indexOf(dot));
  }

  /** Left and right arrow keys move between positions and follow with focus. @param {KeyboardEvent} event */
  #onDotKeydown(event) {
    const step = { ArrowLeft: -1, ArrowRight: 1 }[event.key];
    if (!step) return;
    event.preventDefault();
    this.#stop();
    const index = Math.min(Math.max(this.#current + step, 0), this.#positions.length - 1);
    this.#goTo(index);
    /** @type {HTMLElement | undefined} */ (this.#dots?.children[index])?.focus();
  }

  /** @param {IntersectionObserverEntry} entry */
  #onIntersect(entry) {
    // On screen: half of the carousel shows, or it fills half the screen (a carousel taller than twice the screen
    // can never be half visible).
    const screenHeight = entry.rootBounds?.height ?? window.innerHeight;
    this.#inView = entry.intersectionRatio >= 0.5 || entry.intersectionRect.height >= screenHeight / 2;
    // Fully off screen: back to slide 1, so the next visit starts there.
    if (!entry.isIntersecting && !this.#stopped) this.#goTo(0, { instant: true });
    this.#schedule();
  }

  /** Starts or clears the timer for the next slide, depending on whether the carousel should be rotating. */
  #schedule() {
    clearTimeout(this.#timer);
    this.#timer = undefined;
    const rotating = this.#interval && !this.#stopped && this.#inView && !document.hidden;
    if (!rotating || this.#positions.length <= 1) return;

    this.#timer = window.setTimeout(() => {
      this.#goTo((this.#current + 1) % this.#positions.length);
      this.#schedule();
    }, this.#interval);
  }

  #stop() {
    if (!this.#interval || this.#stopped) return;
    this.#stopped = true;
    this.#updateToggle();
    this.#schedule();
  }

  #play() {
    if (!this.#interval) return;
    this.#stopped = false;
    this.#updateToggle();
    this.#schedule();
  }

  #updateToggle() {
    const toggle = this.#toggle;
    if (!toggle) return;
    toggle.classList.toggle('is-stopped', this.#stopped);
    const label = this.#stopped ? this.dataset.labelPlay : this.dataset.labelPause;
    toggle.setAttribute('aria-label', label || (this.#stopped ? 'Play' : 'Pause'));
  }
}

if (!customElements.get('zw-carousel')) {
  customElements.define('zw-carousel', ZwCarousel);
}
